import { DocumentNode } from 'graphql';
import { client } from './apolloClient';
import { migrationFlags } from './migrationFlags';
import { migrationLogger, MigrationPhase, MigrationStatus } from './migrationLogger';

export interface GraphQLQueryOptions<TVariables = any> {
  query: DocumentNode;
  variables?: TVariables;
  authMode?: string;
}

export interface GraphQLMutationOptions<TVariables = any> {
  mutation: DocumentNode;
  variables?: TVariables;
  authMode?: string;
}

export interface GraphQLResponse<TData = any> {
  data: TData;
  errors?: any[];
}

export async function graphqlQuery<TData = any, TVariables = any>(
  options: GraphQLQueryOptions<TVariables>
): Promise<GraphQLResponse<TData>> {
  if (!migrationFlags.useAwsSdkRepositories) {
    throw new Error('graphqlQuery called but useAwsSdkRepositories flag is disabled');
  }

  try {
    migrationLogger.log(
      MigrationPhase.REPOSITORIES,
      MigrationStatus.IN_PROGRESS,
      'Executing GraphQL query via adapter',
      { hasVariables: !!options.variables }
    );

    const result = await client.query({
      query: options.query,
      variables: options.variables,
      fetchPolicy: 'network-only',
    });

    migrationLogger.log(
      MigrationPhase.REPOSITORIES,
      MigrationStatus.COMPLETED,
      'GraphQL query executed successfully',
      { hasData: !!result.data }
    );

    return {
      data: result.data as TData,
      errors: result.errors ? [...result.errors] : undefined,
    };
  } catch (error) {
    migrationLogger.logError(MigrationPhase.REPOSITORIES, 'GraphQL query failed', error as Error, {
      hasVariables: !!options.variables,
    });
    throw error;
  }
}

export async function graphqlMutation<TData = any, TVariables = any>(
  options: GraphQLMutationOptions<TVariables>
): Promise<GraphQLResponse<TData>> {
  if (!migrationFlags.useAwsSdkRepositories) {
    throw new Error('graphqlMutation called but useAwsSdkRepositories flag is disabled');
  }

  try {
    migrationLogger.log(
      MigrationPhase.REPOSITORIES,
      MigrationStatus.IN_PROGRESS,
      'Executing GraphQL mutation via adapter',
      { hasVariables: !!options.variables }
    );

    const result = await client.mutate({
      mutation: options.mutation,
      variables: options.variables,
    });

    migrationLogger.log(
      MigrationPhase.REPOSITORIES,
      MigrationStatus.COMPLETED,
      'GraphQL mutation executed successfully',
      { hasData: !!result.data }
    );

    return {
      data: result.data as TData,
      errors: result.errors ? [...result.errors] : undefined,
    };
  } catch (error) {
    migrationLogger.logError(
      MigrationPhase.REPOSITORIES,
      'GraphQL mutation failed',
      error as Error,
      { hasVariables: !!options.variables }
    );
    throw error;
  }
}

export const awsApiAdapter = {
  graphqlQuery,
  graphqlMutation,
};
